import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';

import { useAppDispatch } from 'src/hooks/useStore';
import { getApiErrorMessage } from 'src/services/api';
import {
  getStudioDowngradeBlockers,
  getStudioPlanChangeErrorCode,
  PendingPlanChange,
  StudioDowngradeBlocker,
  StudioPlanChangeErrorCode,
  StudioPlanChangeRequest,
  useCancelStudioDowngradeMutation,
  useChangeStudioCreditsMutation,
  useDowngradeStudioPlanMutation,
  useGetCurrentPlanQuery,
  useSetupStudioUpgradeCheckoutMutation,
  useUpgradeStudioPlanMutation,
} from 'src/services/api/studioCreditApi';
import { alertSnackbar } from 'src/store/ui/actions';

import { getStudioPlanTitle } from './studioFeatureGating';
import {
  classifyPlanSelection,
  formatStudioPlanDate,
  StudioPlanSelection,
  toApiInterval,
} from './studioPlanFeatures';
import {
  studioUpgradeReturnUrl,
  useStudioUpgradeCheckoutReturn,
} from './useStudioUpgradeCheckoutReturn';

type PlanChangeModal = 'upgrade' | 'survey' | 'blocked';

interface UseStudioPlanChangeOptions {
  /**
   * Fired when an upgrade leaves nothing left to confirm — it applied, or it
   * was already applied. A surface that opened its own modal to reach the
   * confirmation step closes that modal here; the checkout-setup path does not
   * fire it, since the page is navigating to Stripe.
   */
  onUpgradeComplete?: () => void;
  /** Page to restore after Stripe checkout; defaults to the current pathname. */
  checkoutReturnUrl?: string;
}

const toPlanChangeRequest = (
  selection: StudioPlanSelection,
): StudioPlanChangeRequest => ({
  plan: selection.planId,
  interval: toApiInterval(selection.interval),
  credits: selection.tier.credits,
});

/**
 * The message for a plan change the payment provider refused for a reason the
 * customer can act on: the provider's own wording, which the backend sends as
 * the error message, plus how to fix it. Undefined for every other failure,
 * which keeps the generic copy — provider strings for those name subscription
 * schedules and cancelation semantics and must never reach a toast.
 *
 * Exported for tests; a second payment-failure code is one more case.
 */
export const getPaymentFailureMessage = (
  error: unknown,
): string | undefined => {
  const reason = getApiErrorMessage(error);
  if (!reason) {
    return undefined;
  }
  switch (getStudioPlanChangeErrorCode(error)) {
    case StudioPlanChangeErrorCode.cardDeclined:
      return t(($) => $.page_studio_plans.plan_change.card_declined_error, {
        reason,
      });
    default:
      return undefined;
  }
};

export const useStudioPlanChange = ({
  onUpgradeComplete,
  checkoutReturnUrl,
}: UseStudioPlanChangeOptions = {}) => {
  const dispatch = useAppDispatch();
  const { data: currentPlan, refetch: refetchCurrentPlan } =
    useGetCurrentPlanQuery();
  // Owned here so every surface with plan-change flows confirms a checkout
  // upgrade that lands back on it.
  useStudioUpgradeCheckoutReturn();

  const [upgradePlan, { isLoading: isUpgrading }] =
    useUpgradeStudioPlanMutation();
  const [setupCheckout, { isLoading: isSettingUpCheckout }] =
    useSetupStudioUpgradeCheckoutMutation();
  const [downgradePlan, { isLoading: isDowngrading }] =
    useDowngradeStudioPlanMutation();
  const [changeCredits, { isLoading: isChangingCredits }] =
    useChangeStudioCreditsMutation();
  const [cancelDowngrade, { isLoading: isCancellingDowngrade }] =
    useCancelStudioDowngradeMutation();

  const [selection, setSelection] = useState<StudioPlanSelection>();
  const [activeModal, setActiveModal] = useState<PlanChangeModal>();
  const [blockers, setBlockers] = useState<StudioDowngradeBlocker[]>([]);
  // The scheduled change a just-confirmed downgrade landed on; drives the
  // confirmation step, which outlives the survey modal it replaces.
  const [scheduledDowngrade, setScheduledDowngrade] =
    useState<PendingPlanChange>();

  // Synchronous single-flight latch shared by the three actions: the
  // isLoading-driven disabled states only apply after a rerender, so a rapid
  // double-click needs a check-and-set that happens before any await.
  const inFlightRef = useRef(false);

  const closeModals = useCallback(() => {
    setActiveModal(undefined);
    setSelection(undefined);
  }, []);

  const dismissScheduledDowngrade = useCallback(() => {
    setScheduledDowngrade(undefined);
  }, []);

  const choosePlan = useCallback(
    (chosen: StudioPlanSelection) => {
      const kind = classifyPlanSelection(chosen, currentPlan);
      // Undefined means there's nothing to send: the current plan hasn't
      // resolved to a Studio subscription (CTAs are disabled then, but a
      // stray click must never reach the backend blind) or the selection
      // matches the current plan exactly.
      if (kind === undefined) {
        return;
      }
      // Every give-less selection — a lower plan, fewer credits, or a drop to
      // monthly billing — routes into the same reason survey.
      setSelection(chosen);
      setActiveModal(kind === 'downgrade' ? 'survey' : 'upgrade');
    },
    [currentPlan],
  );

  // The manage-plan flow's downgrade entry point: opens the reason survey
  // directly for its pre-built target (Free, the one plan with no grid card).
  const startDowngrade = useCallback((target: StudioPlanSelection) => {
    setSelection(target);
    setActiveModal('survey');
  }, []);

  // Takes the new TOTAL rather than a plan selection: the credits endpoint
  // needs nothing else, and self-serve Classic has no Studio selection to
  // build.
  const increaseCredits = useCallback(
    async (totalCredits: number) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      try {
        const result = await changeCredits({
          credits: totalCredits,
        }).unwrap();
        dispatch(
          alertSnackbar({
            successMessage: t(
              ($) => $.page_studio_plans.plan_change.credits_increase_success,
              { count: result.credits },
            ),
          }),
        );
      } catch (error) {
        dispatch(
          alertSnackbar({
            errorMessage:
              getPaymentFailureMessage(error) ??
              getApiErrorMessage(error) ??
              t(($) => $.page_studio_plans.plan_change.upgrade_error),
          }),
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [changeCredits, dispatch],
  );

  // A credits-only decrease is give-less, so it schedules at renewal through
  // the downgrade endpoint (no survey — this is not a plan-tier downgrade).
  const decreaseCredits = useCallback(
    async (totalCredits: number) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      try {
        const { pendingChange } = await changeCredits({
          credits: totalCredits,
        }).unwrap();
        dispatch(
          alertSnackbar({
            // A decrease always schedules, so pendingChange is set; the
            // fallback only guards the type when the backend applied the
            // change immediately (a same-total no-op reaching this action).
            successMessage: pendingChange
              ? t(
                  ($) =>
                    $.page_studio_plans.plan_change.credits_decrease_success,
                  {
                    count: pendingChange.credits,
                    date: formatStudioPlanDate(pendingChange.effectiveAt),
                  },
                )
              : t(($) => $.page_studio_plans.plan_change.upgrade_confirming),
          }),
        );
      } catch (error) {
        const blocked = getStudioDowngradeBlockers(error);
        if (blocked) {
          setBlockers(blocked);
          setActiveModal('blocked');
          return;
        }
        dispatch(
          alertSnackbar({
            errorMessage:
              getApiErrorMessage(error) ??
              t(($) => $.page_studio_plans.plan_change.downgrade_error),
          }),
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [changeCredits, dispatch],
  );

  const startCheckout = useCallback(
    async (chosen: StudioPlanSelection) => {
      // Return to this exact page; the marked URL drives the post-checkout
      // confirmation polling.
      const pageUrl =
        checkoutReturnUrl ??
        `${window.location.origin}${window.location.pathname}`;
      const request = toPlanChangeRequest(chosen);
      const { url } = await setupCheckout({
        ...request,
        successUrl: studioUpgradeReturnUrl(pageUrl, {
          plan: request.plan,
          credits: request.credits,
        }),
        cancelUrl: pageUrl,
      }).unwrap();
      window.location.assign(url);
    },
    [checkoutReturnUrl, setupCheckout],
  );

  // Applies a target the caller already holds. Surfaces that pick a plan and
  // submit it in one step (the app-limit paywall) call this directly, so the
  // payment-method, downgrade-flow and no-change handling below is shared with
  // the two-step grid flow rather than reimplemented per surface.
  const submitUpgrade = useCallback(
    async (target: StudioPlanSelection) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      try {
        const result = await upgradePlan(toPlanChangeRequest(target)).unwrap();
        dispatch(
          alertSnackbar({
            successMessage: t(
              ($) => $.page_studio_plans.plan_change.upgrade_success,
              { plan: getStudioPlanTitle(result.plan) },
            ),
          }),
        );
        closeModals();
        onUpgradeComplete?.();
        return;
      } catch (error) {
        // A locked workspace resubscribing onto a plan that drops a feature
        // in use gets the same 409 list as a blocked downgrade. Show that
        // modal; a toast hides the reasons.
        const blocked = getStudioDowngradeBlockers(error);
        if (blocked) {
          setBlockers(blocked);
          setActiveModal('blocked');
          return;
        }
        const code = getStudioPlanChangeErrorCode(error);
        if (code === StudioPlanChangeErrorCode.paymentMethodRequired) {
          // No card on file (Freemium) — collect one through Stripe checkout;
          // the completion webhook performs the upgrade.
          try {
            await startCheckout(target);
            return;
          } catch (checkoutError) {
            const checkoutBlocked = getStudioDowngradeBlockers(checkoutError);
            if (checkoutBlocked) {
              setBlockers(checkoutBlocked);
              setActiveModal('blocked');
              return;
            }
            dispatch(
              alertSnackbar({
                errorMessage:
                  getApiErrorMessage(checkoutError) ??
                  t(($) => $.page_studio_plans.plan_change.upgrade_error),
              }),
            );
            return;
          }
        }
        if (code === StudioPlanChangeErrorCode.useDowngradeFlow) {
          setActiveModal('survey');
          return;
        }
        if (code === StudioPlanChangeErrorCode.noChange) {
          dispatch(
            alertSnackbar({
              successMessage: t(
                ($) => $.page_studio_plans.plan_change.no_change,
              ),
            }),
          );
          closeModals();
          onUpgradeComplete?.();
          return;
        }
        dispatch(
          alertSnackbar({
            errorMessage:
              getPaymentFailureMessage(error) ??
              getApiErrorMessage(error) ??
              t(($) => $.page_studio_plans.plan_change.upgrade_error),
          }),
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [upgradePlan, dispatch, closeModals, startCheckout, onUpgradeComplete],
  );

  // totalCredits overrides the card's chosen total with the upgrade modal's
  // additional-credits selection.
  const confirmUpgrade = useCallback(
    async (totalCredits?: number) => {
      if (!selection) {
        return;
      }
      await submitUpgrade(
        totalCredits !== undefined
          ? { ...selection, tier: { ...selection.tier, credits: totalCredits } }
          : selection,
      );
    },
    [selection, submitUpgrade],
  );

  // reason is the required written answer. The survey modal refuses to
  // confirm until it is non-empty; the endpoint rejects a missing one too.
  const confirmDowngrade = useCallback(
    async (reason: string) => {
      const trimmedReason = reason.trim();
      if (!selection || inFlightRef.current || !trimmedReason) {
        return;
      }
      inFlightRef.current = true;
      try {
        const result = await downgradePlan({
          ...toPlanChangeRequest(selection),
          survey: { otherReason: trimmedReason },
        }).unwrap();
        // The confirmation step takes over from here: it states the plan and
        // the date the change takes effect, which a toast can't hold open.
        closeModals();
        setScheduledDowngrade(result.pendingChange);
      } catch (error) {
        const blocked = getStudioDowngradeBlockers(error);
        if (blocked) {
          setBlockers(blocked);
          setActiveModal('blocked');
          return;
        }
        if (
          getStudioPlanChangeErrorCode(error) ===
          StudioPlanChangeErrorCode.useUpgradeFlow
        ) {
          setActiveModal('upgrade');
          return;
        }
        dispatch(
          alertSnackbar({
            errorMessage:
              getApiErrorMessage(error) ??
              t(($) => $.page_studio_plans.plan_change.downgrade_error),
          }),
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [selection, downgradePlan, dispatch, closeModals],
  );

  const keepCurrentPlan = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    try {
      await cancelDowngrade().unwrap();
      dispatch(
        alertSnackbar({
          successMessage: t(
            ($) => $.page_studio_plans.plan_change.keep_plan_success,
          ),
        }),
      );
    } catch (error) {
      if (
        getStudioPlanChangeErrorCode(error) ===
        StudioPlanChangeErrorCode.noPendingDowngrade
      ) {
        // Already cancelled (e.g. from another tab) — refresh to drop the
        // stale banner.
        void refetchCurrentPlan();
        return;
      }
      dispatch(
        alertSnackbar({
          errorMessage:
            getApiErrorMessage(error) ??
            t(($) => $.page_studio_plans.plan_change.keep_plan_error),
        }),
      );
    } finally {
      inFlightRef.current = false;
    }
  }, [cancelDowngrade, dispatch, refetchCurrentPlan]);

  return {
    currentPlan,
    selection,
    activeModal,
    blockers,
    isUpgrading: isUpgrading || isSettingUpCheckout,
    isDowngrading,
    isChangingCredits,
    isCancellingDowngrade,
    scheduledDowngrade,
    dismissScheduledDowngrade,
    choosePlan,
    startDowngrade,
    increaseCredits,
    decreaseCredits,
    submitUpgrade,
    confirmUpgrade,
    confirmDowngrade,
    keepCurrentPlan,
    closeModals,
  };
};
